import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { getUtmParams, trackEvent } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";

const OBJECTIFS = [
  "Générer plus de leads",
  "Obtenir des leads plus qualifiés",
  "Convertir davantage de leads en clients",
  "Structurer notre création de contenu",
  "Améliorer nos campagnes publicitaires",
  "Bâtir un système marketing complet",
  "Comprendre ce qui devrait être priorisé",
];

const REVENUS = [
  "Moins de 250 000 $",
  "250 000 $ à 500 000 $",
  "500 000 $ à 1 M$",
  "1 M$ à 5 M$",
  "Plus de 5 M$",
];

const BUDGETS = [
  "Aucun investissement",
  "Moins de 1 000 $",
  "1 000 $ à 3 000 $",
  "3 000 $ à 7 500 $",
  "Plus de 7 500 $",
];

const DECISION = ["Oui", "Oui, avec un associé ou une équipe", "Non"];

type FormState = {
  prenom: string;
  nom: string;
  entreprise: string;
  courriel: string;
  telephone: string;
  web: string;
  
  objectif: string;
  blocage: string;
  revenu: string;
  budget: string;
  capacite: string;
  decision: string;
  consentContact: boolean;
  consentMarketing: boolean;
  honeypot: string;
};

const initialState: FormState = {
  prenom: "",
  nom: "",
  entreprise: "",
  courriel: "",
  telephone: "",
  web: "",
  
  objectif: "",
  blocage: "",
  revenu: "",
  budget: "",
  capacite: "",
  decision: "",
  consentContact: false,
  consentMarketing: false,
  honeypot: "",
};

const stepOneSchema = z.object({
  prenom: z.string().trim().min(1, "Le prénom est requis").max(80),
  nom: z.string().trim().min(1, "Le nom est requis").max(80),
  entreprise: z.string().trim().min(1, "Le nom de l'entreprise est requis").max(120),
  courriel: z.string().trim().email("Adresse courriel invalide").max(255),
  telephone: z
    .string()
    .trim()
    .refine((v) => v.replace(/\D/g, "").length >= 10, "Numéro de téléphone invalide"),
  web: z.string().trim().max(255).optional().or(z.literal("")),
  
});

const stepTwoSchema = z.object({
  objectif: z.string().min(1, "Sélectionnez votre principal objectif"),
  blocage: z.string().trim().min(1, "Décrivez brièvement ce qui bloque").max(1500),
  revenu: z.string().min(1, "Sélectionnez une fourchette"),
  budget: z.string().min(1, "Sélectionnez une fourchette"),
  capacite: z.string().trim().min(1, "Cette information est requise").max(200),
  decision: z.string().min(1, "Sélectionnez une réponse"),
  consentContact: z.literal(true, { errorMap: () => ({ message: "Votre consentement est requis" }) }),
});

const fieldCls =
  "h-12 bg-background border-border text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-ring md:h-10";

const ErrorText = ({ children }: { children?: string }) =>
  children ? (
    <p role="alert" className="mt-1.5 text-xs text-destructive">
      {children}
    </p>
  ) : null;

const RadioGroupField = ({
  legend,
  name,
  options,
  value,
  onChange,
  error,
}: {
  legend: string;
  name: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
  error?: string;
}) => (
  <fieldset>
    <legend className="text-sm font-medium text-foreground">{legend}</legend>
    <div className="mt-3 grid gap-2 md:grid-cols-2">
      {options.map((option) => {
        const checked = value === option;
        return (
          <label
            key={option}
            className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors ${
              checked ? "border-primary bg-primary/10 text-foreground" : "border-border bg-background text-muted-foreground hover:border-border/80"
            }`}
          >
            <input
              type="radio"
              name={name}
              value={option}
              checked={checked}
              onChange={() => onChange(option)}
              className="h-4 w-4 accent-[hsl(var(--primary))]"
            />
            {option}
          </label>
        );
      })}
    </div>
    <ErrorText>{error}</ErrorText>
  </fieldset>
);

const LeadFormV2 = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const startedRef = useRef(false);
  const mountedAt = useMemo(() => Date.now(), []);
  const stepTopRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (step === 2) stepTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackEvent("lead_form_started");
    }
    setData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key as string]) return prev;
      const next = { ...prev };
      delete next[key as string];
      return next;
    });
  };

  const applyErrors = (result: z.SafeParseReturnType<unknown, unknown>) => {
    if (result.success) return true;
    const next: Record<string, string> = {};
    result.error.issues.forEach((issue) => {
      const key = String(issue.path[0]);
      if (!next[key]) next[key] = issue.message;
    });
    setErrors(next);
    return false;
  };

  const goToStepTwo = () => {
    if (!applyErrors(stepOneSchema.safeParse(data))) return;
    trackEvent("lead_form_step_completed", { step: 1 });
    setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!applyErrors(stepTwoSchema.safeParse(data))) return;

    // Antispam : champ leurre + soumission trop rapide
    if (data.honeypot.trim() !== "" || Date.now() - mountedAt < 3000) return;

    setSubmitting(true);
    trackEvent("lead_form_step_completed", { step: 2 });

    const payload = {
      ...data,
      honeypot: undefined,
      utm: getUtmParams(),
      page: window.location.pathname,
      submitted_at: new Date().toISOString(),
    };

    try {
      sessionStorage.setItem("a8020_derniere_demande", JSON.stringify(payload));
    } catch {
      // stockage indisponible : la soumission continue quand même
    }

    // Notification courriel à l'équipe
    try {
      const { error } = await supabase.functions.invoke("notify-homepage-lead-email", {
        body: {
          prenom: data.prenom,
          nom: data.nom,
          entreprise: data.entreprise,
          courriel: data.courriel,
          telephone: data.telephone,
          web: data.web,
          objectif: data.objectif,
          blocage: data.blocage,
          revenu: data.revenu,
          budget: data.budget,
          capacite: data.capacite,
          decision: data.decision,
          consentMarketing: data.consentMarketing,
          submissionId: `${Date.now()}-${data.courriel}`,
        },
      });
      if (error) console.warn("Notification courriel échouée:", error);
    } catch (err) {
      console.warn("Notification courriel échouée:", err);
    }


    trackEvent("lead_form_submitted", { objectif: data.objectif, revenu: data.revenu });
    setSubmitting(false);
    navigate("/merci");
  };

  return (
    <section id="formulaire" aria-labelledby="formulaire-titre" className="scroll-mt-24 py-14 md:py-28">
      <div ref={stepTopRef} className="mx-auto max-w-[1180px] px-5 md:px-8">
        <div className="grid gap-8 md:grid-cols-[38%_1fr] md:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="md:sticky md:top-28 md:self-start"
          >
            <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-primary">
              Premier contact
            </p>
            <div aria-hidden="true" className="mt-5 h-px w-16 bg-border" />
            <h2
              id="formulaire-titre"
              className="mt-5 text-[1.7rem] font-semibold leading-[1.12] tracking-tight text-foreground md:mt-6 md:text-[2.75rem]"
            >
              Voyons où votre entreprise est rendue.
            </h2>
            <p className="mt-4 text-[0.95rem] leading-[1.55] text-muted-foreground md:mt-5 md:text-base">
              Remplissez le court formulaire. Ça va nous permettre de comprendre rapidement votre situation et ce que
              vous cherchez à améliorer.
            </p>
            <p className="mt-4 text-sm text-primary">Aucun pitch préparé. On commence par comprendre.</p>
          </motion.div>

        <form onSubmit={handleSubmit} noValidate className="rounded-2xl border border-border/50 bg-secondary/30 p-5 md:p-9">


          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 md:text-xs">
            Étape {step} de 2 — {step === 1 ? "Coordonnées" : "Situation"}
          </p>
          <div aria-hidden="true" className="mt-4 h-px w-full bg-border">
            <div className="h-px bg-primary transition-all duration-500" style={{ width: step === 1 ? "50%" : "100%" }} />
          </div>

          {/* Champ leurre antispam */}
          <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
            <label htmlFor="site-secondaire">Ne pas remplir</label>
            <input
              id="site-secondaire"
              name="site-secondaire"
              tabIndex={-1}
              autoComplete="off"
              value={data.honeypot}
              onChange={(e) => setData((prev) => ({ ...prev, honeypot: e.target.value }))}
            />
          </div>

          <div className={step === 1 ? "mt-6 space-y-4 md:mt-8 md:space-y-5" : "hidden"}>
            <div className="grid gap-4 md:grid-cols-2 md:gap-5">
              <div>
                <Label htmlFor="prenom">Prénom *</Label>
                <Input id="prenom" className={fieldCls} value={data.prenom} onChange={(e) => set("prenom", e.target.value)} autoComplete="given-name" maxLength={80} />
                <ErrorText>{errors.prenom}</ErrorText>
              </div>
              <div>
                <Label htmlFor="nom">Nom *</Label>
                <Input id="nom" className={fieldCls} value={data.nom} onChange={(e) => set("nom", e.target.value)} autoComplete="family-name" maxLength={80} />
                <ErrorText>{errors.nom}</ErrorText>
              </div>
            </div>

            <div>
              <Label htmlFor="entreprise">Entreprise *</Label>
              <Input id="entreprise" className={fieldCls} value={data.entreprise} onChange={(e) => set("entreprise", e.target.value)} autoComplete="organization" maxLength={120} />
              <ErrorText>{errors.entreprise}</ErrorText>
            </div>

            <div className="grid gap-4 md:grid-cols-2 md:gap-5">
              <div>
                <Label htmlFor="courriel">Courriel *</Label>
                <Input id="courriel" type="email" inputMode="email" className={fieldCls} value={data.courriel} onChange={(e) => set("courriel", e.target.value)} autoComplete="email" maxLength={255} />
                <ErrorText>{errors.courriel}</ErrorText>
              </div>
              <div>
                <Label htmlFor="telephone">Téléphone *</Label>
                <Input id="telephone" type="tel" inputMode="tel" className={fieldCls} value={data.telephone} onChange={(e) => set("telephone", e.target.value)} autoComplete="tel" maxLength={25} />
                <ErrorText>{errors.telephone}</ErrorText>
              </div>
            </div>

            <div>
              <Label htmlFor="web">Site Web ou réseau social</Label>
              <Input id="web" inputMode="url" className={fieldCls} value={data.web} onChange={(e) => set("web", e.target.value)} placeholder="exemple.com ou @entreprise" maxLength={255} />
              <ErrorText>{errors.web}</ErrorText>
            </div>


            <Button type="button" onClick={goToStepTwo} size="lg" className="group w-full rounded-full bg-primary py-5 text-base text-primary-foreground hover:bg-primary/90 md:py-6">
              Continuer
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>

          <div className={step === 2 ? "mt-6 space-y-7 md:mt-8 md:space-y-8" : "hidden"}>
            <RadioGroupField legend="Quel est votre principal objectif actuellement? *" name="objectif" options={OBJECTIFS} value={data.objectif} onChange={(v) => set("objectif", v)} error={errors.objectif} />

            <div>
              <Label htmlFor="blocage">Qu'est-ce qui semble bloquer le plus présentement? *</Label>
              <Textarea id="blocage" className={`${fieldCls} mt-2 min-h-[110px] resize-none`} value={data.blocage} onChange={(e) => set("blocage", e.target.value)} maxLength={1500} />
              <ErrorText>{errors.blocage}</ErrorText>
            </div>

            <RadioGroupField legend="Quel est approximativement le chiffre d'affaires annuel de l'entreprise? *" name="revenu" options={REVENUS} value={data.revenu} onChange={(v) => set("revenu", v)} error={errors.revenu} />

            <RadioGroupField legend="Quel est votre investissement marketing mensuel actuel? *" name="budget" options={BUDGETS} value={data.budget} onChange={(v) => set("budget", v)} error={errors.budget} />

            <div>
              <Label htmlFor="capacite">Combien de nouveaux clients pourriez-vous servir dans les 90 prochains jours? *</Label>
              <Input id="capacite" className={`${fieldCls} mt-2`} value={data.capacite} onChange={(e) => set("capacite", e.target.value)} maxLength={200} />
              <ErrorText>{errors.capacite}</ErrorText>
            </div>

            <RadioGroupField legend="Participez-vous à la décision finale? *" name="decision" options={DECISION} value={data.decision} onChange={(v) => set("decision", v)} error={errors.decision} />

            <div className="space-y-4 rounded-2xl border border-border bg-background p-5">
              <div className="flex items-start gap-3">
                <Checkbox id="consentContact" checked={data.consentContact} onCheckedChange={(v) => set("consentContact", v === true)} className="mt-0.5" />
                <Label htmlFor="consentContact" className="text-sm font-normal leading-relaxed text-muted-foreground">
                  J'autorise Agence 80/20 à me contacter par courriel, téléphone ou message texte au sujet de ma demande. *
                </Label>
              </div>
              <ErrorText>{errors.consentContact}</ErrorText>
              <div className="flex items-start gap-3">
                <Checkbox id="consentMarketing" checked={data.consentMarketing} onCheckedChange={(v) => set("consentMarketing", v === true)} className="mt-0.5" />
                <Label htmlFor="consentMarketing" className="text-sm font-normal leading-relaxed text-muted-foreground">
                  J'accepte de recevoir occasionnellement des communications marketing d'Agence 80/20. Facultatif.
                </Label>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row-reverse">
              <Button type="submit" size="lg" disabled={submitting} className="group w-full rounded-full bg-primary py-5 md:py-6 text-base text-primary-foreground hover:bg-primary/90">
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Envoyer ma demande
              </Button>
              <Button type="button" variant="outline" size="lg" onClick={() => setStep(1)} className="w-full rounded-full border-border py-5 md:py-6 text-base text-foreground hover:bg-secondary sm:w-auto sm:px-8">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour
              </Button>
            </div>

            <p className="text-sm text-muted-foreground">
              Un membre de notre équipe examinera votre demande et vous contactera pour en apprendre davantage sur votre
              situation.
            </p>
          </div>
        </form>
        </div>
      </div>

    </section>
  );
};

export default LeadFormV2;
